import { CartLinesUpdateEvent, StandardEvents } from "@shopify/events";
import { reconcileGifts } from "./cpp-gift-with-purchase.js";

const root = window.Shopify?.routes?.root || "/";
const state = {};
const pending = new Set();
let running;
let dirty = false;
let failure;
let replayingCheckout = false;
const ownEvents = new WeakSet();

async function request(url, body) {
  const response = await fetch(url, {
    method: body ? "POST" : "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    signal: AbortSignal.timeout(15000),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json();
  if (!response.ok || result.errors || result.status)
    throw new Error(
      "We could not update your complimentary gifts. Please refresh your cart and try again.",
    );
  return result;
}

const io = {
  async load() {
    const url = new URL(root, location.origin);
    url.searchParams.set("sections", "cpp-gift-with-purchase");
    url.searchParams.set("_cpp_gwp", String(Date.now()));
    const result = await request(url);
    const doc = new DOMParser().parseFromString(
      result["cpp-gift-with-purchase"] || "",
      "text/html",
    );
    const config = doc.querySelector("[data-cpp-gift-config]");
    if (!config)
      throw new Error("We could not check your complimentary gifts. Please refresh your cart.");
    return JSON.parse(config.textContent);
  },
  update: (updates) => request(`${root}cart/update.js`, { updates }),
  add: (items) => request(`${root}cart/add.js`, { items }),
  async publish(cart) {
    const ids = [
      ...new Set(
        [...document.querySelectorAll("cart-items-component[data-section-id]")].map(
          (element) => element.dataset.sectionId,
        ),
      ),
    ].slice(0, 5);
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set("sections", ids.join(","));
    const sections = ids.length ? await request(url) : {};
    const event = new CartLinesUpdateEvent({
      action: "update",
      context: "cart",
      lines: [],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: "cpp-gift-with-purchase",
          sections,
          items: cart.items,
          itemCount: cart.item_count,
          didError: false,
        },
      }),
    });
    ownEvents.add(event);
    document.dispatchEvent(event);
  },
};

function showError(error) {
  document.documentElement.toggleAttribute("data-cpp-gift-failed", Boolean(error));
  document.querySelectorAll("[data-cpp-gift-error]").forEach((node) => node.remove());
  if (!error) return;
  const targets = document.querySelectorAll(".cart__ctas");
  for (const target of targets.length
    ? targets
    : [document.querySelector("main") || document.body]) {
    const message = document.createElement("p");
    message.className = "cpp-gift-error";
    message.dataset.cppGiftError = "";
    message.setAttribute("role", "alert");
    message.textContent = error.message;
    target.prepend(message);
  }
}

function schedule() {
  dirty = true;
  document.documentElement.setAttribute("data-cpp-gift-busy", "");
  if (!running) {
    running = Promise.resolve()
      .then(async () => {
        while (dirty) {
          dirty = false;
          while (pending.size) await Promise.allSettled([...pending]);
          // Web Locks also serialize this manager across tabs that share a cart.
          if (navigator.locks)
            await navigator.locks.request("cpp-gift-with-purchase", () =>
              reconcileGifts(io, state),
            );
          else await reconcileGifts(io, state);
        }
        failure = null;
        showError(null);
      })
      .catch((error) => {
        failure = error;
        showError(error);
        console.warn("[CPP gifts]", error);
      })
      .finally(() => {
        running = null;
        document.documentElement.removeAttribute("data-cpp-gift-busy");
        if (dirty && !failure) schedule();
      });
  }
  return running;
}

function track(promise) {
  const settled = Promise.resolve(promise)
    .catch(() => {})
    .finally(() => {
      pending.delete(settled);
      dirty = true;
    });
  pending.add(settled);
  schedule();
}

for (const eventName of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(eventName, (event) => {
    if (!ownEvents.has(event)) track(event.promise);
  });
}
// Horizon debounces quantity changes for 300 ms before its cart event fires.
document.addEventListener("quantity-selector:update", () =>
  track(new Promise((resolve) => setTimeout(resolve, 350))),
);
document.addEventListener("cart:updated", () => schedule());
window.addEventListener("pageshow", () => schedule());
window.addEventListener("focus", () => schedule());

async function guardCheckout(event, resume) {
  if (replayingCheckout) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  await schedule();
  // Recheck if another mutation was queued as the previous transaction settled.
  while (running) await running;
  if (failure) return;
  replayingCheckout = true;
  try {
    resume();
  } finally {
    replayingCheckout = false;
  }
}

document.addEventListener(
  "click",
  (event) => {
    const target =
      event.target instanceof Element ? event.target.closest('[name="checkout"], a[href]') : null;
    if (!target) return;
    if (target.matches("a") && !new URL(target.href).pathname.match(/\/checkout\/?$/)) return;
    guardCheckout(event, () => {
      // Cart sections may have been replaced during the reconciliation.
      const replacement = target.isConnected ? target : document.querySelector('[name="checkout"]');
      replacement?.click();
    });
  },
  true,
);
document.addEventListener(
  "submit",
  (event) => {
    if (!event.submitter?.matches('[name="checkout"]')) return;
    const form = event.target;
    const submitter = event.submitter;
    guardCheckout(event, () => {
      const current = submitter.isConnected
        ? submitter
        : document.querySelector('[name="checkout"]');
      (current?.form || form).requestSubmit(current || undefined);
    });
  },
  true,
);

schedule();
